import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { AsUserFragment, NoteDto } from '@pf/shared/util-platform';
import {
    DocumentDto,
    IDocumentDto,
    IDocumentsFacade,
    ProfileProvider,
} from '@pf/shared-common';
import { joinStrings } from '@pf/shared-utility';
import { orderBy } from 'lodash-es';
import { NzCommentModule } from 'ng-zorro-antd/comment';
import { NzListModule } from 'ng-zorro-antd/list';
import {
    DocumentFormComponent,
    DocumentsModule,
    IDocumentForm,
    PlatformFormsModule,
    SharedUiModule,
} from '@pf/shared-ui';
import { FormControl, Validators } from '@angular/forms';
import { catchError, first, Observable, of, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { DateService } from '@pf/shared-services';

interface NoteThread {
    user: string;
    date: string;
    text: string;
    documentUrl$?: Observable<string>;
    documentIsImage: boolean;
}

@Component({
    selector: 'platform-notes-thread',
    standalone: true,
    imports: [
        NzListModule,
        NzCommentModule,
        SharedUiModule,
        PlatformFormsModule,
        DocumentsModule,
        AsyncPipe,
    ],
    templateUrl: './notes-thread.component.html',
    styleUrl: './notes-thread.component.scss',
})
export class NotesThreadComponent {
    @ViewChild('form', { static: false }) form!: DocumentFormComponent;
    noteInputControl = new FormControl<string>('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(250),
    ]);

    private readonly profileProvider = inject(ProfileProvider);
    private readonly dateService = inject(DateService);

    entityId = input.required<string>();
    notes = input.required<NoteDto[]>();
    readonly = input<boolean>(false);
    documentFacade = input.required<IDocumentsFacade>();
    noteAdded = output<NoteDto>();
    documentAdded = output<DocumentDto>();

    documents = signal<DocumentDto[]>([]);
    selectedDocument = signal<IDocumentDto | null>(null);
    uploadingDocument = signal<boolean>(false);

    thread = computed(() => {
        const thread: NoteThread[] = [];

        this.notes().forEach(note => {
            thread.push({
                user: joinStrings(
                    ' ',
                    note.user?.firstName,
                    note.user?.lastName
                ),
                date: this.dateService.formatDateTime(note.timestamp),
                text: note.note,
                documentIsImage: false,
            });
        });

        this.documents().forEach(document => {
            const fileName = document.fileName?.toLowerCase();
            const isImage =
                !!fileName &&
                (fileName.endsWith('.png') ||
                    fileName.endsWith('.jpg') ||
                    fileName.endsWith('.jpeg') ||
                    fileName.endsWith('.gif') ||
                    fileName.endsWith('.bmp'));
            thread.push({
                user: document.fileName || 'Document Upload',
                date: this.dateService.formatDateTime(document.createdDate),
                text: document.notes || '',
                documentUrl$: this.documentFacade().previewUrl$(
                    this.entityId(),
                    document.id
                ),
                documentIsImage: isImage,
            });
        });

        return orderBy(thread, t => new Date(t.date), 'desc');
    });

    constructor() {
        effect(() => {
            this.loadDocuments();
        });
    }

    open(): void {
        this.selectedDocument.set(new DocumentDto({}));
        this.form.open();
    }

    close(): void {
        this.selectedDocument.set(null);
        this.form.close();
    }

    onDocumentUploadSubmit(formValue: IDocumentForm) {
        if (!this.form.valid) {
            return;
        }
        if (!this.selectedDocument()) {
            this.close();
            return;
        }
        this.uploadingDocument.set(true);
        const document: IDocumentDto = new DocumentDto({
            file: formValue.file as File,
            fileName: formValue.documentName as string,
            expirationDate: formValue.documentExpirationDate as string,
            notes:
                (formValue.documentNotes || '').trim() +
                '\n' +
                this.getUploadUserInfo(),
            description: formValue.documentDescription as string,
        });
        this.createDocument(document)
            .pipe(
                first(),
                tap(() => {
                    this.uploadingDocument.set(false);
                }),
                catchError(() => {
                    this.uploadingDocument.set(false);
                    return of(null);
                })
            )
            .subscribe(() => this.close());
    }

    addNote() {
        this.noteAdded.emit(
            new NoteDto({
                note: this.noteInputControl.value,
                timestamp: new Date().toISOString(),
                user: AsUserFragment(this.profileProvider.profile),
            })
        );
        this.noteInputControl.reset();
    }

    private loadDocuments() {
        this.documentFacade()
            .all$(this.entityId().toUpperCase())
            .subscribe(documents => {
                this.documents.set(documents);
            });
    }

    private createDocument(document: IDocumentDto) {
        return this.documentFacade()
            .create$(this.entityId(), document)
            .pipe(
                tap(created => {
                    this.documents.update(documents => [...documents, created]);
                    this.documentAdded.emit(created);
                })
            );
    }

    private getUploadUserInfo() {
        return `-- uploaded by ${this.profileProvider.profile.fullName}`;
    }
}
